(() => {
  'use strict';

  const init = () => {
    const drawer = document.querySelector('#CartDrawer.gc-drawer[data-cart-sep26]');
    if (!drawer || drawer.dataset.cartSep26Ready) return;

    const form = drawer.querySelector('form');
    const products = drawer.querySelector('[data-products]');
    const summary = drawer.querySelector('[data-cart-summary]');
    const upsells = drawer.querySelector('[data-gc-upsells]');
    if (!form || !products || !summary) return;
    drawer.dataset.cartSep26Ready = 'true';

    const root = String(window.Shopify?.routes?.root || window.theme?.routes?.home || '/').replace(/\/?$/, '/');
    const errorBox = drawer.querySelector('[data-gc-error]');
    const defaultError = errorBox?.dataset.default || 'Your cart could not be updated. Please try again.';
    const checkoutSelector = '[name="checkout"], .cart__checkout';
    const mutationSelector = '.js-qty__num, .js-qty__adjust, .js-qty__adjust2, .removeIcon, [data-gc-upsell]';
    const disabledControls = new Map();
    let queue = Promise.resolve();
    let pending = 0;
    let needsRefresh = false;
    let installmentMarkup = null;
    let giftSyncPromise = null;

    const showError = message => {
      if (!errorBox) return;
      errorBox.textContent = message || '';
      errorBox.hidden = !message;
      if (message && needsRefresh) {
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.dataset.gcRetry = '';
        retry.textContent = errorBox.dataset.retry || 'Try again';
        errorBox.append(' ', retry);
      }
    };

    const plainText = html => {
      const node = document.createElement('span');
      node.innerHTML = String(html || '');
      return node.textContent || '';
    };

    const formatMoney = cents => {
      if (window.theme?.Currency?.formatMoney) {
        return plainText(window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat));
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    };

    const handlesFor = card => (card.dataset.handles || '').split(',').map(handle => handle.trim()).filter(Boolean);
    const itemRows = () => Array.from(products.querySelectorAll('.cart__item[data-line-key]'));
    const matchesCard = (card, item) => item.properties?._gc_upsell === 'sep26'
      && (handlesFor(card).includes(item.handle)
        || String(item.variant_id || item.id) === card.dataset.variantId);

    const setBusy = () => {
      const busy = pending > 0;
      drawer.classList.toggle('is-updating', busy);
      form.setAttribute('aria-busy', String(busy));
      disabledControls.forEach((disabled, control) => {
        if (!control.isConnected) disabledControls.delete(control);
      });
      drawer.querySelectorAll(mutationSelector + ', ' + checkoutSelector).forEach(control => {
        const shouldDisable = busy || (needsRefresh && control.matches(checkoutSelector));
        if (shouldDisable) {
          if (!disabledControls.has(control)) disabledControls.set(control, control.disabled);
          control.disabled = true;
        } else if (disabledControls.has(control)) {
          control.disabled = disabledControls.get(control);
          disabledControls.delete(control);
        }
      });
      // The checkout assignment runtime owns inert on the native wallet itself.
      // A separate parent gate lets it finish readiness checks during cart updates.
      drawer.querySelectorAll('[data-gc-wallet-guard]').forEach(guard => {
        guard.inert = busy || needsRefresh;
        guard.setAttribute('aria-busy', String(busy || needsRefresh));
      });
    };

    const sync = () => {
      const details = summary.querySelector('[data-gc-summary]');
      if (!details) return;
      const total = Number(details.dataset.total) || 0;
      const count = Number(details.dataset.count) || 0;
      drawer.classList.toggle('is-empty', count === 0);

      drawer.querySelectorAll('[data-gc-item-count]').forEach(badge => {
        const label = count === 1 ? badge.dataset.singular : badge.dataset.plural;
        badge.textContent = (label || '[count] items').replace('[count]', String(count));
      });
      document.querySelectorAll('.cart-link__bubble-num').forEach(badge => { badge.textContent = String(count); });
      document.querySelectorAll('.cart-link__bubble').forEach(bubble => {
        bubble.classList.toggle('cart-link__bubble--visible', count > 0);
      });
      drawer.querySelectorAll('[data-gc-checkout-total]').forEach(node => {
        node.textContent = details.dataset.money || formatMoney(total);
      });
      drawer.querySelectorAll('[data-subtotal], [data-gc-estimated-total]').forEach(node => {
        node.dataset.cartSubtotal = String(total);
      });

      const shipping = drawer.querySelector('[data-gc-shipping]');
      if (shipping) {
        const threshold = Number(shipping.dataset.threshold) || 0;
        const remaining = Math.max(0, threshold - total);
        shipping.hidden = threshold <= 0 || count === 0;
        shipping.classList.toggle('gc-shipping--unlocked', remaining === 0);
        const message = shipping.querySelector('.gc-shipping-message');
        if (message) {
          message.textContent = plainText(remaining === 0
            ? shipping.dataset.unlocked
            : (shipping.dataset.locked || '').replace('[amount]', formatMoney(remaining)));
        }
      }

      const rows = itemRows();
      drawer.querySelectorAll('[data-gc-upsell]').forEach(card => {
        const selected = rows.some(row => matchesCard(card, {
          handle: row.getAttribute('handle'), variant_id: row.dataset.key,
          properties: { _gc_upsell: row.dataset.gcUpsellLine }
        }));
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-pressed', String(selected));
        card.querySelectorAll('.gc-upsell-plus').forEach(icon => { icon.toggleAttribute('hidden', selected); });
        card.querySelectorAll('.gc-upsell-check').forEach(icon => { icon.toggleAttribute('hidden', !selected); });
      });

      const returns = drawer.querySelector('[data-gc-returns]');
      if (returns) {
        returns.textContent = details.dataset.finalSale === 'true'
          ? returns.dataset.finalSale : returns.dataset.standard;
      }
      const source = summary.querySelector('[data-gc-installments]');
      const installments = drawer.querySelector('.gc-installments');
      if (source && installments && installmentMarkup !== source.innerHTML) {
        installmentMarkup = source.innerHTML;
        installments.innerHTML = installmentMarkup;
      }
      setBusy();
    };

    const request = async (path, payload, asText = false) => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await window.fetch(root + path, {
          method: payload ? 'POST' : 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
          headers: {
            Accept: asText ? 'text/html' : 'application/json',
            ...(payload ? { 'Content-Type': 'application/json' } : {})
          },
          ...(payload ? { body: JSON.stringify(payload) } : {})
        });
        if (asText) {
          if (!response.ok) throw new Error(defaultError);
          return await response.text();
        }
        const data = await response.json();
        if (!response.ok || Number(data.status) >= 400) {
          throw new Error(data.description || data.message || defaultError);
        }
        return data;
      } catch (error) {
        if (error.name === 'AbortError') throw new Error(errorBox?.dataset.timeout || defaultError);
        if (error.name === 'TypeError' || error.name === 'SyntaxError') throw new Error(defaultError);
        throw error;
      } finally {
        window.clearTimeout(timeout);
      }
    };

    const rememberFocus = control => {
      const row = control.closest('.cart__item');
      // An added offer leaves the list, so focus moves to the Remove button of its new row.
      const card = control.closest('[data-gc-upsell]');
      return {
        control,
        key: row?.dataset.lineKey,
        variant: row?.dataset.key || card?.dataset.variantId,
        upsell: row?.dataset.gcUpsellLine || (card ? 'sep26' : undefined),
        index: row ? itemRows().indexOf(row) : 0,
        action: control.matches('.js-qty__num') ? 'input'
          : control.matches('.js-qty__adjust--plus') ? 'plus'
          : control.matches('.removeIcon') || card ? 'remove' : 'minus',
        card,
        selectionStart: control.matches('.js-qty__num') ? control.selectionStart : null,
        selectionEnd: control.matches('.js-qty__num') ? control.selectionEnd : null
      };
    };

    const restoreFocus = focus => {
      if (!focus) return;
      const active = document.activeElement;
      if (active && active !== document.body && active !== focus.control && !disabledControls.has(active)) return;
      const rows = itemRows();
      const row = rows.find(item => item.dataset.lineKey === focus.key)
        || rows.find(item => item.dataset.key === focus.variant && item.dataset.gcUpsellLine === focus.upsell)
        || rows[Math.min(focus.index, rows.length - 1)];
      const selectors = {
        input: '.js-qty__num', plus: '.js-qty__adjust--plus',
        minus: '.gibbon-cart-item__minus, .js-qty__adjust--minus, .lastQuantity', remove: '.removeIcon'
      };
      // Offers are re-rendered with the cart: find the same offer again if it is still listed.
      const card = focus.card?.isConnected ? focus.card
        : focus.card && Array.from(drawer.querySelectorAll('[data-gc-upsell]'))
          .find(item => item.dataset.variantId === focus.card.dataset.variantId);
      const control = card
        || row?.querySelector(selectors[focus.action]) || row?.querySelector('.js-qty__num, .removeIcon')
          || drawer.querySelector('.js-drawer-close');
      if (!control || control.disabled) return;
      control.focus({ preventScroll: true });
      if (focus.action === 'input' && control.matches('.js-qty__num') && focus.selectionStart !== null) {
        control.setSelectionRange(focus.selectionStart, focus.selectionEnd);
      }
    };

    const waitForGifts = controller => new Promise((resolve, reject) => {
      const deadline = Date.now() + 20000;
      const check = () => {
        if (!controller.gwpSyncInFlight) return resolve();
        if (Date.now() >= deadline) return reject(new Error(errorBox?.dataset.timeout || defaultError));
        window.setTimeout(check, 100);
      };
      check();
    });

    const syncGifts = async (controller, items) => {
      let timeout;
      if (!giftSyncPromise) {
        giftSyncPromise = Promise.resolve().then(() => controller.syncGwpItems(items))
          .finally(() => { giftSyncPromise = null; });
      }
      try {
        return await Promise.race([
          giftSyncPromise,
          new Promise((resolve, reject) => {
            timeout = window.setTimeout(() => reject(new Error(errorBox?.dataset.timeout || defaultError)), 20000);
          })
        ]);
      } finally {
        window.clearTimeout(timeout);
      }
    };

    const refresh = async (includeGifts = true, waitForExistingGifts = true) => {
      const [html, cart] = await Promise.all([request('cart?view=sep26', null, true), request('cart.js')]);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextProducts = parsed.querySelector('.cart__items');
      const nextSummary = parsed.querySelector('[data-cart-summary]');
      const details = nextSummary?.querySelector('[data-gc-summary]');
      // A simultaneous external add can land between the HTML and JSON reads.
      // Never unlock checkout with totals from two different cart revisions.
      if (!nextProducts || !details || Number(details.dataset.count) !== cart.item_count
        || Number(details.dataset.total) !== cart.total_price) throw new Error(defaultError);

      const cartForm = form.gibbonCartForm;
      if (includeGifts && typeof cartForm?.syncGwpItems === 'function') {
        // The legacy hook returns `true` immediately when another gift add is
        // running. Wait for it before checking the newly changed cart ourselves.
        if (cartForm.gwpSyncInFlight) {
          if (!waitForExistingGifts) throw new Error(defaultError);
          await waitForGifts(cartForm);
          return refresh(true, false);
        }
        if (giftSyncPromise || nextProducts.querySelector('.cart__item[gwp="true"]')) {
          const addedGifts = await syncGifts(cartForm, nextProducts);
          if (addedGifts) {
            await waitForGifts(cartForm);
            // One gift pass per refresh chain; the helper also rebuilds the
            // legacy cart. Read its resulting totals before enabling checkout.
            return refresh(false, false);
          }
        }
      }
      products.replaceChildren(nextProducts);
      summary.innerHTML = nextSummary.innerHTML;
      // Offers depend on the cart lines, so they come from the same render.
      const nextUpsells = parsed.querySelector('[data-gc-upsells]');
      if (upsells && nextUpsells) upsells.innerHTML = nextUpsells.innerHTML;
      needsRefresh = false;
      sync();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      return cart;
    };

    const enqueue = (work, focus) => {
      pending += 1;
      showError('');
      setBusy();
      queue = queue.then(async () => {
        try {
          // Network errors can be ambiguous: the server may already have applied
          // the mutation. Keep checkout gated until a matching refresh succeeds.
          needsRefresh = true;
          await work();
          await refresh();
        } catch (error) {
          // Shopify can adjust an over-inventory request before returning an error.
          // Re-read the cart to restore its actual quantity and any affected prices.
          try { await refresh(); } catch (refreshError) {
            products.querySelectorAll('.js-qty__num').forEach(input => { input.value = input.defaultValue; });
          }
          showError(error.message || defaultError);
        } finally {
          pending -= 1;
          setBusy();
          restoreFocus(focus);
        }
      });
    };

    const changeQuantity = (input, value, control = input) => {
      const row = input.closest('.cart__item[data-line-key]');
      if (!row) return;
      const quantity = Number(value);
      if (!/^\d+$/.test(String(value).trim()) || !Number.isSafeInteger(quantity) || quantity < 0) {
        input.value = input.defaultValue;
        showError(errorBox?.dataset.invalidQuantity || defaultError);
        return;
      }
      if (quantity === Number(input.defaultValue)) return;
      const key = row.dataset.lineKey;
      enqueue(() => request('cart/change.js', { id: key, quantity }), rememberFocus(control));
    };

    drawer.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target : event.target.parentElement;
      const control = target?.closest('[data-gc-retry], [data-gc-upsell], .js-qty__adjust, .js-qty__adjust2, .removeIcon');
      if (!control || !drawer.contains(control)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (control.disabled || pending) return;

      if (control.matches('[data-gc-retry]')) {
        enqueue(() => Promise.resolve(), null);
        return;
      }

      if (control.matches('[data-gc-upsell]')) {
        enqueue(async () => {
          const cart = await request('cart.js');
          const matching = (cart.items || []).filter(item => matchesCard(control, item));
          if (matching.length) {
            const updates = {};
            matching.forEach(item => { updates[item.key] = 0; });
            return request('cart/update.js', { updates });
          }
          if (!control.dataset.variantId) throw new Error(defaultError);
          return request('cart/add.js', {
            items: [{ id: control.dataset.variantId, quantity: 1, properties: { _gc_upsell: 'sep26' } }]
          });
        }, rememberFocus(control));
        return;
      }

      const row = control.closest('.cart__item');
      if (control.matches('.removeIcon')) {
        const key = row?.dataset.lineKey;
        if (key) enqueue(() => request('cart/change.js', { id: key, quantity: 0 }), rememberFocus(control));
        return;
      }
      const input = row?.querySelector('.js-qty__num');
      if (!input) return;
      const current = Number(input.defaultValue) || 0;
      const quantity = Math.max(0, current + (control.matches('.js-qty__adjust--plus') ? 1 : -1));
      changeQuantity(input, quantity, control);
    }, true);

    drawer.addEventListener('change', event => {
      if (!event.target.matches('.js-qty__num')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!pending) changeQuantity(event.target, event.target.value.trim());
    }, true);

    drawer.addEventListener('keydown', event => {
      if (event.key !== 'Enter' || !event.target.matches('.js-qty__num')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!pending) changeQuantity(event.target, event.target.value.trim());
    }, true);

    form.addEventListener('submit', event => {
      if (pending || needsRefresh || (!event.submitter && document.activeElement?.matches('.js-qty__num'))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    new MutationObserver(sync).observe(summary, { childList: true, subtree: true });
    new MutationObserver(sync).observe(products, { childList: true });
    document.addEventListener('drawerOpen.CartDrawer', () => {
      sync();
      if (needsRefresh && !pending) enqueue(() => Promise.resolve(), null);
    });
    sync();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
